import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { cn } from 'lib/utils'
import { useAnalytics, useTheme } from 'hooks'
import { useEngagementGate } from 'modules/engagement/hooks/use-engagement-gate'
import {
  CalendarDays,
  CreditCard,
  FolderOpen,
  Gift,
  Handshake,
  Home,
  KeyRound,
  MessageCircleHeart,
  Moon,
  Palette,
  Plug,
  Sun,
  Users,
  X,
  Zap,
} from 'lucide-react'
import { useReferralProgram } from 'modules/referrals/use-referral-program'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { intercom } from 'services/intercom/intercom.service'
import { BusinessSwitcher } from '../components/BusinessSwitcher'
import { UserProfileFooter } from '../components/UserProfileFooter'

// ─── Nav Items ──────────────────────────────────────────────────────────────

const primaryNavItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/posts', label: 'Posts', icon: FolderOpen },
  // Comment automation wizard — hidden while the `engagement_section` gate is off.
  {
    path: '/engagement',
    label: 'Auto Comments & DMs',
    icon: Zap,
    gate: 'engagement_section',
  },
] as const

const businessSettingsNavItems = [
  { path: '/brand', label: 'Brand', icon: Palette },
  { path: '/integrations', label: 'Integrations', icon: Plug },
  { path: '/collaborators', label: 'Collaborators', icon: Users },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/api-keys', label: 'API & MCP', icon: KeyRound },
] as const

const themeOptions = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
] as const

const sectionEyebrowClass = 'mono text-paper-faint mb-2 px-3 text-[10px] tracking-[0.2em] uppercase'

const utilityRowClass =
  'text-paper-dim hover:bg-bg-3 hover:text-paper flex w-full items-center gap-3 rounded-[8px] px-3 py-2 text-[13.5px] transition-colors'

// ─── Sidebar ────────────────────────────────────────────────────────────────

interface AppSidebarProps {
  isMobile?: boolean
  onClose?: () => void
}

/**
 * Primary product chrome — left rail with business switcher up top, two nav
 * groups (Main, Business), a bottom-anchored utility cluster (support,
 * what's new, theme picker), and the user profile footer.
 *
 * Active state is bg-bg-3 + Pool chrome bar/icon (text-primary + bg-accent-chrome
 * on light; Acid on dark) + 2px left bar. No full acid chip in the rail — one
 * loud CTA per view lives on the page (Send, Approve, etc.).
 */
export function AppSidebar({ isMobile, onClose }: AppSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { track } = useAnalytics()
  const { theme, setTheme } = useTheme()
  // Reactive gate via the engagement module's shared hook — same
  // useGateValue-plus-settled semantics as /engagement, one source of truth.
  const { enabled: engagementEnabled } = useEngagementGate()
  // Referral program gate + server enabled flag. Hidden entirely when off so
  // there's no dead menu entry (matches the /referrals page guard). `info`
  // also drives the "approved rewards waiting to be claimed" acid dot on the
  // nav item so the user notices the reward without a modal (celebration
  // handles the modal itself; this is the passive nudge for revisits).
  const { enabled: referralsEnabled, info: referralInfo } = useReferralProgram()
  const referralClaimableCount = referralInfo?.claimable.length ?? 0

  // Gated nav items resolve against their section's reactive gate hook; an
  // unknown gate value hides the item (fail closed for nav, the page itself
  // guards too).
  const gateEnabled = (gate: string) => gate === 'engagement_section' && engagementEnabled

  const visiblePrimaryNavItems = primaryNavItems.filter(
    item => !('gate' in item) || gateEnabled(item.gate)
  )
  // No business-group item is gated today (API & MCP launched ungated).
  const visibleBusinessNavItems = businessSettingsNavItems

  const [unreadCount, setUnreadCount] = useState(() => intercom.getUnreadCount())
  useEffect(() => intercom.onUnreadCountChange(setUnreadCount), [])

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
      if (isMobile && onClose) onClose()
    },
    [navigate, isMobile, onClose]
  )

  const handleSupport = useCallback(() => {
    intercom.show()
    if (isMobile && onClose) onClose()
  }, [isMobile, onClose])

  const handleWhatsNew = useCallback(() => {
    window.open('https://taka.ai/whats-new', '_blank', 'noopener,noreferrer')
    if (isMobile && onClose) onClose()
  }, [isMobile, onClose])

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const renderNavItem = ({
    path,
    label,
    icon: Icon,
    showDot,
  }: {
    path: string
    label: string
    icon: React.ElementType
    /** Passive nudge: renders a small acid dot on the icon (used by referrals). */
    showDot?: boolean
  }) => {
    const active = isActive(path)
    return (
      <button
        key={path}
        onClick={() => {
          track(AnalyticsEvents.Sidebar.SIDEBAR_NAV_TAPPED, {
            destination: path === '/' ? 'home' : path.replace('/', ''),
          })
          handleNavigate(path)
        }}
        className={cn(
          'relative flex w-full items-center gap-3 rounded-[8px] px-3 py-2 text-[13.5px] transition-colors',
          // Active = bg-bg-3 + Pool/Acid chrome bar + text-primary icon (v3.2).
          active
            ? 'bg-bg-3 font-medium text-paper'
            : 'text-paper-dim hover:bg-bg-3 hover:text-paper'
        )}
      >
        {active && (
          <span
            aria-hidden
            className="bg-accent-chrome absolute top-1.5 bottom-1.5 left-0 w-[2px] rounded-r-[2px]"
          />
        )}
        <span className="relative inline-flex shrink-0">
          <Icon
            size={18}
            strokeWidth={active ? 2.2 : 1.8}
            className={cn('shrink-0', active && 'text-primary')}
          />
          {showDot && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-acid shadow-[0_0_0_1.5px_var(--bg-2)]"
            />
          )}
        </span>
        <span className="text-left leading-tight">{label}</span>
      </button>
    )
  }

  return (
    // The wrapper div provides the surface; the parent (AppLayout) provides
    // the right border so the sidebar reads as a column rather than a card.
    // Mobile: bg-2 already comes from AppLayout drawer wrapper.
    <div className="flex h-full w-64 flex-col bg-bg-2 text-paper">
      {/* ── Business Switcher ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-line px-1 py-1">
        <div className="min-w-0 flex-1">
          <BusinessSwitcher />
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="mr-1 rounded-[8px] p-1.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* ── Primary Navigation ────────────────────────────────────────── */}
      <div className="space-y-0.5 p-3">
        <div className={sectionEyebrowClass}>Main</div>
        {visiblePrimaryNavItems.map(renderNavItem)}
      </div>

      {/* ── Business ──────────────────────────────────────────────────── */}
      <div className="space-y-0.5 border-t border-line p-3">
        <div className={sectionEyebrowClass}>Business</div>
        {visibleBusinessNavItems.map(renderNavItem)}
      </div>

      {/* ── Spacer ────────────────────────────────────────────────────── */}
      <div className="flex-1" />

      {/* ── Utility row (actions, not navigation) ─────────────────────── */}
      <div className="space-y-0.5 border-t border-line p-3">
        <button
          type="button"
          onClick={handleSupport}
          className={utilityRowClass}
          aria-label="Get support"
        >
          <span className="relative inline-flex">
            <MessageCircleHeart size={18} strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-[color:var(--warning)] px-0.5 text-[8px] font-bold text-[color:var(--ink-on-acid)]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </span>
          Get Support
        </button>
        <button
          type="button"
          onClick={handleWhatsNew}
          className={utilityRowClass}
          aria-label="What's new"
        >
          <Gift size={18} strokeWidth={1.8} />
          What's new
        </button>
        {/* Refer a friend — nav item (has active state), lives in the utility
            cluster so the offer stays visible without crowding Business. */}
        {referralsEnabled &&
          renderNavItem({
            path: '/referrals',
            label: 'Refer a friend',
            icon: Handshake,
            showDot: referralClaimableCount > 0,
          })}
        <div
          role="tablist"
          aria-label="Theme"
          className="mt-1 flex rounded-[10px] border border-line bg-bg-3 p-[3px]"
        >
          {themeOptions.map(({ value, label, icon: Icon }) => {
            const active = theme === value
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-[7px] px-3 py-1.5 text-[12px] font-medium transition-colors',
                  active ? 'bg-bg text-paper' : 'text-paper-dim hover:text-paper'
                )}
              >
                <Icon size={14} />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── User Profile Footer ───────────────────────────────────────── */}
      <div className="border-t border-line px-1 py-1">
        <UserProfileFooter onNavigate={handleNavigate} />
      </div>
    </div>
  )
}
